import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { ErrorBoundary } from "react-error-boundary";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import Routes from "../routes";
import { AxiosInterceptors } from "../routes/AxiosInterceptors";
import "../styles/global.scss";
import "../styles/lib.scss";
import SegmentHistoryListener from "./SegmentHistoryListener";
import RumHistoryListener from "./RumHistoryListener";
import "./i18n";

import "@stripe/stripe-js"; //Recommended by Stripe to put the import here (see https://github.com/stripe/stripe-js)
import "moment/min/locales";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { ErrorFallback } from "../common/components/ErrorFallback/ErrorFallback";
import { AppLoadingProvider, GlobalLoadingProvider } from "../context";
import { SpecsProvider } from "../context/Specs/SpecsProvider";
import { theme } from "../styles/theme";
import { MultiplayerProvider } from "../context/Multiplayer/MultiplayerProvider";
import { ProjectProvider } from "../context/Project";
import { TeamProvider } from "../context/Team";
import { DesignProvider } from "../context/Design/DesignProvider";
import { CollaboratorsProvider } from "../context/Collaborators";
import { TenantProvider } from "../context/Tenant";
import { CustomDragLayer } from "../components/Design/DesignCard/CustomDragLayer";
import { getSupportWidgetMode } from "../common/supportWidget";
import ShowMeWidget from "./ShowMeWidget";

const mode = getSupportWidgetMode();

const App = () => {
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const langFromParams = searchParams.get("lang");

  useEffect(() => {
    if (langFromParams) {
      i18n.changeLanguage(langFromParams);
    }
    // eslint-disable-next-line
  }, [langFromParams]);

  return (
    <DndProvider backend={HTML5Backend}>
      <ThemeProvider theme={theme}>
        {mode === "showme" && <ShowMeWidget />}
        <TenantProvider>
          <GlobalLoadingProvider>
            <AppLoadingProvider>
              <SpecsProvider>
                <MultiplayerProvider>
                  <TeamProvider>
                    <CollaboratorsProvider>
                      <ProjectProvider>
                        <DesignProvider>
                          <ErrorBoundary FallbackComponent={ErrorFallback}>
                            <RumHistoryListener>
                              <SegmentHistoryListener>
                                <Box>
                                  <CssBaseline>
                                    <AxiosInterceptors />
                                    <Routes />
                                    <CustomDragLayer />
                                  </CssBaseline>
                                </Box>
                                <ToastContainer
                                  position="bottom-center"
                                  pauseOnFocusLoss={false}
                                  newestOnTop
                                  style={{ width: "800px" }}
                                />
                              </SegmentHistoryListener>
                            </RumHistoryListener>
                          </ErrorBoundary>
                        </DesignProvider>
                      </ProjectProvider>
                    </CollaboratorsProvider>
                  </TeamProvider>
                </MultiplayerProvider>
              </SpecsProvider>
            </AppLoadingProvider>
          </GlobalLoadingProvider>
        </TenantProvider>
      </ThemeProvider>
    </DndProvider>
  );
};

export default App;
